<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InboxesAPI from 'dashboard/api/inboxes';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});
const emit = defineEmits(['inbox-refresh']);

const { t } = useI18n();
const accessToken = ref('');
const groupId = ref('');
const isSaving = ref(false);

const save = async () => {
  const token = accessToken.value.trim();
  const gid = groupId.value.trim();
  // Don't fire a no-op PATCH that would still re-validate against the existing
  // (possibly broken) token and confuse the admin with a server error.
  if (!token && !gid) {
    useAlert(t('INBOX_MGMT.ADD.VK_CALLBACK.NOTHING_TO_UPDATE'));
    return;
  }

  isSaving.value = true;
  try {
    const channel = {};
    if (token) channel.access_token = token;
    if (gid) channel.group_id = gid;
    await InboxesAPI.update(props.inbox.id, { channel });
    useAlert(t('INBOX_MGMT.ADD.VK_CALLBACK.SAVED'));
    accessToken.value = '';
    groupId.value = '';
    emit('inbox-refresh');
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      t('INBOX_MGMT.ADD.VK_CHANNEL.CREATE_ERROR');
    useAlert(msg);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="mb-4 rounded-lg border border-n-slate-6 bg-n-slate-2 p-4 flex flex-col gap-3">
    <div>
      <h4 class="text-sm font-medium text-n-slate-12 mb-1">
        {{ $t('INBOX_MGMT.ADD.VK_CALLBACK.UPDATE_TOKEN_TITLE') }}
      </h4>
      <p class="text-xs text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.VK_CALLBACK.UPDATE_TOKEN_HINT') }}
      </p>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.VK_CHANNEL.ACCESS_TOKEN.LABEL') }}
      </label>
      <input
        v-model="accessToken"
        type="password"
        autocomplete="new-password"
        :placeholder="$t('INBOX_MGMT.ADD.VK_CALLBACK.ACCESS_TOKEN_PLACEHOLDER')"
        class="w-full rounded-md border border-n-slate-6 bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.VK_CHANNEL.GROUP_ID.LABEL') }}
        <span class="text-n-slate-10">({{ inbox.group_id || $t('INBOX_MGMT.ADD.VK_CALLBACK.CURRENT_VALUE') }})</span>
      </label>
      <input
        v-model="groupId"
        type="text"
        :placeholder="String(inbox.group_id || '')"
        class="w-full rounded-md border border-n-slate-6 bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
      />
    </div>

    <div class="flex justify-end">
      <NextButton
        :is-loading="isSaving"
        :disabled="!accessToken.trim() && !groupId.trim()"
        type="button"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.VK_CALLBACK.SAVE_BUTTON')"
        @click="save"
      />
    </div>
  </div>
</template>
